import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

/**
 * Retry interceptor — survives high-latency and intermittent connections.
 *
 * Default policy (safe, idempotent only):
 *   - GET / HEAD / OPTIONS: retried up to 3 times on transient failure
 *     (status 0 network error, 408 timeout, 429 too-many-requests, or any 5xx).
 *   - POST / PUT / PATCH / DELETE: NOT retried — they may have already
 *     hit the server. Callers that know the action is idempotent can
 *     opt in with the `X-Retry: true` header.
 *   - Any caller can opt out with `X-Retry: false`.
 *
 * Backoff schedule: 1s, 2s, 4s (capped at 8s) with ±25% jitter so a
 * fleet of clients reconnecting after a brief outage doesn't dogpile
 * the server.
 *
 * The `X-Retry` hint header is stripped before the request leaves the
 * browser so it never reaches the backend or appears in CDN logs.
 */

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 8_000;
const RETRYABLE_STATUSES = new Set([0, 408, 429]);
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const optIn = req.headers.get('X-Retry');
  const stripped = req.headers.has('X-Retry')
    ? req.clone({ headers: req.headers.delete('X-Retry') })
    : req;

  if (optIn === 'false') {
    return next(stripped);
  }

  const eligible = optIn === 'true' || SAFE_METHODS.has(req.method.toUpperCase());
  if (!eligible) {
    return next(stripped);
  }

  return next(stripped).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error, attempt) => retryDelay(error, attempt, stripped),
    }),
  );
};

function retryDelay(
  error: unknown,
  attempt: number,
  _req: HttpRequest<unknown>,
): Observable<number> {
  if (!isTransient(error)) {
    return throwError(() => error);
  }
  const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** (attempt - 1));
  const jitter = exp * (0.75 + Math.random() * 0.5);
  return timer(jitter);
}

function isTransient(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  if (RETRYABLE_STATUSES.has(error.status)) return true;
  return error.status >= 500 && error.status < 600;
}
